import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from "@/components/ui/select";
import { useSettings } from "@/context/SettingsContext";
import { useWebsites } from "@/context/WebsitesContext";
import { getImageModelOptions, getImageModelConfig } from "@/config/imageModels";

export default function AIImageGenerationCard() {
  const {
    postingFrequency,
    writingStyle,
    subjectMatters,
    formattemplate,
    imagePrompt,
    setImagePrompt,
    imageModel,
    setImageModel,
    negativePrompt,
    setNegativePrompt,
    weeklyPlanningDay,
    contentPolicies,
    isLoading: settingsLoading,
    updateSettingsInDatabase,
  } = useSettings();
  const { currentWebsite, updateWebsite } = useWebsites();

  return (
    <Card id="images">
      <CardHeader>
        <CardTitle>AI image generation</CardTitle>
        <CardDescription>
          Configure AI-powered image generation for your posts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Enable AI Image Generation</Label>
              <p className="text-sm text-muted-foreground">
                Automatically generate preview images for your posts using AI
              </p>
            </div>
            <Switch
              checked={currentWebsite?.enable_ai_image_generation ?? false}
              onCheckedChange={async (checked) => {
                if (!currentWebsite) {
                  toast.error("Please select a website first");
                  return;
                }
                try {
                  await updateWebsite(currentWebsite.id, {
                    enable_ai_image_generation: checked,
                  });
                  toast.success(
                    checked
                      ? "AI image generation enabled"
                      : "AI image generation disabled"
                  );
                } catch (error) {
                  console.error("Failed to update AI image generation setting:", error);
                  toast.error("Failed to update AI image generation setting");
                }
              }}
            />
          </div>

          {currentWebsite?.enable_ai_image_generation && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="image-model">
                  Image Generation Model
                  <span className="block text-sm text-muted-foreground">
                    Select which AI model to use for generating images
                  </span>
                </Label>
                <Select
                  value={imageModel || "gpt-image-1"}
                  onValueChange={(value) => {
                    setImageModel(value);
                    const modelConfig = getImageModelConfig(value);
                    toast.success(
                      `Image model updated to ${modelConfig?.name || value}`
                    );
                  }}
                >
                  <SelectTrigger id="image-model">
                    <SelectValue placeholder="Select image generation model">
                      {(() => {
                        const modelConfig = getImageModelConfig(imageModel || "gpt-image-1");
                        return modelConfig?.name || imageModel || "GPT Image 1";
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {getImageModelOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">{option.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="image-prompt">
                  Image Generation Prompt
                  <span className="block text-sm text-muted-foreground">
                    Customize how images are generated. Use {"{title}"} for the content
                    title and {"{content}"} for the content description.
                  </span>
                </Label>
                <Input
                  id="image-prompt"
                  type="text"
                  value={imagePrompt}
                  onChange={(e) => {
                    setImagePrompt(e.target.value);
                  }}
                  onBlur={async () => {
                    try {
                      await updateSettingsInDatabase(
                        postingFrequency,
                        writingStyle,
                        subjectMatters,
                        formattemplate,
                        imagePrompt,
                        imageModel,
                        negativePrompt,
                        weeklyPlanningDay
                      );
                      toast.success("Image generation prompt updated");
                    } catch (error) {
                      console.error("Failed to update image prompt:", error);
                      toast.error("Failed to update image prompt");
                    }
                  }}
                  placeholder="Create a modern, professional image that represents: {title}. Context: {content}"
                />
                <p className="text-xs text-muted-foreground">
                  Example: "Create a photorealistic {"{title}"} in the style of{" "}
                  {"{content}"}"
                </p>
              </div>

              {imageModel === "stable-diffusion" && (
                <div className="space-y-2">
                  <Label htmlFor="negative-prompt">
                    Negative Prompt (Stable Diffusion)
                    <span className="block text-sm text-muted-foreground">
                      Specify what you don't want to appear in the generated image
                    </span>
                  </Label>
                  <Input
                    id="negative-prompt"
                    type="text"
                    defaultValue={negativePrompt}
                    onBlur={async (e) => {
                      try {
                        setNegativePrompt(e.target.value);
                        toast.success("Negative prompt updated");
                      } catch (error) {
                        console.error("Failed to update negative prompt:", error);
                        toast.error("Failed to update negative prompt");
                      }
                    }}
                    placeholder="blurry, bad quality, distorted faces, bad anatomy"
                  />
                  <p className="text-xs text-muted-foreground">
                    Example: "low quality, bad proportions, deformed, blurry, grainy"
                  </p>
                </div>
              )}



              <div className="rounded-lg border bg-muted/30 p-4">
                <h4 className="font-medium mb-2">About AI Image Generation:</h4>
                <ul className="list-disc pl-4 space-y-1 text-sm text-muted-foreground">
                  <li>Generates unique preview images for each post</li>
                  <li>Uses post content to create relevant images</li>
                  <li>Images are automatically optimized for your website</li>
                  <li>Requires valid content to generate images</li>
                  {imageModel === "stable-diffusion" && (
                    <li>Stable Diffusion allows more control with negative prompts</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
